import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { AdditionalOption, DropdownChoice } from '../types';
import { formatPricingPreview } from '../utils/pricingUtils';

interface DropdownChoiceManagerProps {
  option: AdditionalOption;
  currency: string;
  onUpdateOption: (id: string, updates: Partial<AdditionalOption>) => void;
  onAddPricing: (optionId: string, isCore: boolean, optionType: string, choiceIndex?: number) => void;
  onRemovePricing: (optionId: string, isCore: boolean, pricingIndex: number) => void;
  renderPricingPreview: (pricing: any) => string | null;
}

export function DropdownChoiceManager({
  option,
  currency,
  onUpdateOption,
  onAddPricing,
  onRemovePricing,
  renderPricingPreview
}: DropdownChoiceManagerProps) {
  const currencySymbol = currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : '$';

  const addChoicePricing = (choiceIndex: number) => {
    if (!option.options || option.type !== 'select') return;

    const newOptions = [...option.options];
    const choice = newOptions[choiceIndex];
    
    if (!choice.pricing) {
      choice.pricing = [];
    }

    // Add a default pricing configuration
    choice.pricing.push({
      type: 'fixed',
      value: 0,
      description: `Additional cost for ${choice.value}`
    });

    onUpdateOption(option.id, { options: newOptions });
    toast.success('Pricing added to choice');
  };

  const removeChoicePricing = (choiceIndex: number, pricingIndex: number) => {
    if (!option.options || option.type !== 'select') return;

    const newOptions = [...option.options];
    const choice = newOptions[choiceIndex];
    
    if (choice.pricing) {
      choice.pricing.splice(pricingIndex, 1);
      onUpdateOption(option.id, { options: newOptions });
      toast.success('Pricing removed from choice');
    }
  };

  const updateChoicePricing = (choiceIndex: number, pricingIndex: number, updates: Partial<{type: 'fixed' | 'percentage' | 'per_person' | 'per_day' | 'per_person_day', value: number, description: string}>) => {
    if (!option.options || option.type !== 'select') return;

    const newOptions = [...option.options];
    const choice = newOptions[choiceIndex];
    
    if (choice.pricing && choice.pricing[pricingIndex]) {
      choice.pricing[pricingIndex] = { ...choice.pricing[pricingIndex], ...updates };
      onUpdateOption(option.id, { options: newOptions });
    }
  };

  // Choice pricing is managed directly here for simplicity

  const updateChoiceValue = (choiceIndex: number, value: string) => {
    if (!option.options) return;
    
    const newOptions = [...option.options];
    newOptions[choiceIndex] = { ...newOptions[choiceIndex], value };
    onUpdateOption(option.id, { options: newOptions });
  };

  const addChoice = () => {
    const newOptions = [...(option.options || [])];
    newOptions.push({ 
      value: `Option ${newOptions.length + 1}`, 
      pricing: [] 
    });
    onUpdateOption(option.id, { options: newOptions });
  };

  const removeChoice = (choiceIndex: number) => {
    if (!option.options) return;
    
    const newOptions = option.options.filter((_, idx) => idx !== choiceIndex);
    onUpdateOption(option.id, { options: newOptions });
    toast.success('Choice removed');
  };

  if (option.type !== 'select' || !option.options) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-slate-700">Choices & Pricing</Label>
        <Button
          variant="outline"
          size="sm"
          onClick={addChoice}
          className="h-8 px-3 text-xs"
        >
          <Plus className="w-3 h-3 mr-1" />
          Add Choice
        </Button>
      </div>
      
      {option.options.map((choice, choiceIdx) => (
        <div key={choiceIdx} className="p-3 bg-slate-50 rounded-lg border">
          <div className="flex items-center justify-between mb-2">
            <Input
              value={choice.value}
              onChange={(e) => updateChoiceValue(choiceIdx, e.target.value)}
              className="flex-1 mr-2 h-8 text-sm"
              placeholder="Choice name..."
            />
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => addChoicePricing(choiceIdx)}
                className="h-8 px-3 text-xs"
              >
                <DollarSign className="w-3 h-3" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => removeChoice(choiceIdx)}
                className="h-8 px-3 text-xs text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>

          {/* Choice Pricing Display & Editor */}
          {choice.pricing && choice.pricing.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-slate-200">
              <div className="text-xs font-medium text-slate-600 mb-1">Pricing for this choice:</div>
              {choice.pricing.map((pricing, pricingIdx) => (
                <div key={pricingIdx} className="p-2 bg-emerald-50 border border-emerald-200 rounded space-y-2">
                  {/* Pricing Type & Value Row */}
                  <div className="flex items-center gap-2">
                    <select
                      value={pricing.type}
                      onChange={(e) => updateChoicePricing(choiceIdx, pricingIdx, { type: e.target.value as 'fixed' | 'percentage' | 'per_person' | 'per_day' | 'per_person_day' })}
                      className="text-xs border rounded px-2 py-1 flex-shrink-0"
                    >
                      <option value="fixed">Fixed</option>
                      <option value="per_person">Per Person</option>
                      <option value="per_day">Per Day</option>
                      <option value="per_person_day">Per Person/Day</option>
                      <option value="percentage">Percentage</option>
                    </select>
                    
                    <div className="flex items-center gap-1 flex-1">
                      {pricing.type === 'percentage' ? (
                        <>
                          <Input
                            type="number"
                            value={pricing.value}
                            onChange={(e) => updateChoicePricing(choiceIdx, pricingIdx, { value: parseFloat(e.target.value) || 0 })}
                            className="w-16 h-7 text-xs"
                            min="0"
                            max="100"
                            step="1"
                          />
                          <span className="text-xs text-slate-500">%</span>
                        </>
                      ) : (
                        <>
                          <span className="text-xs text-slate-500">{currencySymbol}</span>
                          <Input
                            type="number"
                            value={pricing.value}
                            onChange={(e) => updateChoicePricing(choiceIdx, pricingIdx, { value: parseFloat(e.target.value) || 0 })}
                            className="w-16 h-7 text-xs"
                            min="0"
                            step="0.01"
                          />
                        </>
                      )}
                      
                      <span className="text-xs font-medium text-emerald-800 flex-1 ml-2">
                        = {formatPricingPreview(pricing, currency)}
                      </span>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeChoicePricing(choiceIdx, pricingIdx)}
                      className="h-6 w-6 p-0 text-red-500 hover:text-red-700 flex-shrink-0"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  
                  {/* Description Row */}
                  <Input
                    value={pricing.description || ''}
                    onChange={(e) => updateChoicePricing(choiceIdx, pricingIdx, { description: e.target.value })}
                    placeholder="Optional description..."
                    className="w-full h-6 text-xs"
                  />
                </div>
              ))}
            </div>
          )}

          {/* No pricing message */}
          {(!choice.pricing || choice.pricing.length === 0) && (
            <div className="pt-2 border-t border-slate-200">
              <div className="text-xs text-slate-500 italic">
                No pricing configured for this choice
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
